<template>
  <div>
    <h3 class="mb-4">Manage Teams for {{ locationName }}</h3>
    <div class="container mt-5">
      <h2 class="text-center mb-4">Manage Teams</h2>
      <div v-if="teams.length > 0" class="mt-4">
        <h3>Teams Registered for Today</h3>
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Team Name</th>
              <th>Team ID</th>
              <th>Check-In Bonus Points</th>
              <th>Manual Score Adjustment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="team in teams" :key="team.id">
              <td>{{ team.teamName }}</td>
              <td>{{ team.teamId }}</td>
              <td>
                <input 
                  type="checkbox" 
                  v-model="team.surveyBonus" 
                  @change="onBonusChange(team.id, 'surveyBonus', $event.target.checked)"
                />
              </td>
              <td>
                <div class="d-flex align-items-center gap-2">
                  <input 
                    type="number" 
                    v-model="team.manualAdjustment" 
                    class="form-control form-control-sm" 
                    style="width: 100px;"
                    placeholder="Points"
                  >
                  <button 
                    @click="adjustScore(team.id, team.manualAdjustment)" 
                    class="btn btn-primary btn-sm"
                    :disabled="!team.manualAdjustment"
                  >
                    Apply
                  </button>
                </div>
              </td>
              <td>
                <button @click="deleteTeam(team.id)" class="btn btn-danger btn-sm">
                  Delete Team
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        <p>No teams registered for the selected location and date.</p>
      </div>
    </div>
  </div>
</template>


  
  <script>
  import { ref, onMounted } from 'vue';
  import { db } from '@/firebase';
  import { collection, query, where, getDocs, deleteDoc, doc, setDoc } from 'firebase/firestore';
  
  export default {
    name: 'ManageTeams',
    props: {
      selectedLocation: {
        type: String,
        required: true,
      },
    },
    setup(props) {
      const teams = ref([]);
      const gameId = ref('');
  
      const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
  
      const fetchTeams = async () => {
        try {
          // Query the game document for the selected location and date
          const gameQuery = query(
            collection(db, 'games'),
            where('location', '==', props.selectedLocation),
            where('date', '==', getTodayDate())
          );
          const gameSnapshot = await getDocs(gameQuery);
  
          if (!gameSnapshot.empty) {
            const gameDoc = gameSnapshot.docs[0];
            gameId.value = gameDoc.id;
  
            // Query teams registered for this game
            const teamsQuery = query(
              collection(db, 'teams'),
              where('location', '==', props.selectedLocation),
              where('gameId', '==', gameId.value)
            );
  
            const teamsSnapshot = await getDocs(teamsQuery);
            teams.value = teamsSnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            await fetchBonusPoints();
          } else {
            console.log('No game found for the selected location and date.');
          }
        } catch (error) {
          console.error('Error fetching teams:', error);
        }
      };

      // New function to fetch bonus points
const fetchBonusPoints = async () => {
  try {
    const bonusPointsQuery = query(
      collection(db, 'games', gameId.value, 'submissions'),
      where('teamId', 'in', teams.value.map(team => team.id))
    );
    const bonusPointsSnapshot = await getDocs(bonusPointsQuery);

    bonusPointsSnapshot.forEach((doc) => {
      const data = doc.data();
      const team = teams.value.find(t => t.id === data.teamId);
      if (team) {
        team[data.bonusType] = true; // Add properties to the team object
      }
    });
  } catch (error) {
    console.error('Error fetching bonus points:', error);
  }
};

      const deleteTeam = async (teamId) => {
        if (confirm('Are you sure you want to delete this team?')) {
          try {
            await deleteDoc(doc(db, 'teams', teamId));
            alert('Games deleted successfully.');
            await fetchTeams(); // Refresh the games list
          } catch (error) {
            console.error('Error deleting team:', error);
            alert('Error deleting team. Please try again.');
          }
        }
      };

      const toggleBonusPoints = async (teamId, bonusType, isChecked) => {
  try {
    // Ensure gameId and teamId are strings
    const teamIdStr = String(teamId);

    // Determine the point value based on the bonus type
    const bonusPoints = bonusType === 'checkInBonus' ? 20 : 50;

    // Construct the document ID based on the bonus type
    const bonusDocId = `${teamIdStr}_bonus_${bonusType === 'checkInBonus' ? 'checkin' : 'survey'}`;

    // Log the values to ensure they are correct
    console.log("gameId:", gameId.value);
    console.log("teamId:", teamIdStr);
    console.log("bonusDocId:", bonusDocId);

    // Reference to the document in the submissions subcollection
    const bonusDocRef = doc(db, 'games', gameId.value, 'submissions', bonusDocId);
    console.log(bonusDocRef)
    if (isChecked) {
      // Create or update the document with the bonus points
      await setDoc(bonusDocRef, {
        teamId: teamIdStr,
        bonusType: bonusType,
        totalScore: bonusPoints,
        isBonus: true,
        timestamp: new Date(), // Optionally store the time of the check-in
      });
    } else {
      // Delete the document if the checkbox is unchecked
      await deleteDoc(bonusDocRef);
    }

    // Refresh the teams list to reflect the updated data (if needed)
    await fetchTeams();
  } catch (error) {
    console.error(`Error updating ${bonusType} for team ${teamId}:`, error);
  }
};

const onBonusChange = (teamId, bonusType, isChecked) => {
  toggleBonusPoints(teamId, bonusType, isChecked);
};

const adjustScore = async (teamId, points) => {
  if (!points || !teamId) return;
  
  const adjustment = parseInt(points);
  if (isNaN(adjustment)) return;

  try {
    // Create the document ID for the manual adjustment
    const adjustmentId = `${teamId}_manual_adjustment_${Date.now()}`;
    
    // Reference to the document in the submissions subcollection
    const adjustmentDocRef = doc(db, 'games', gameId.value, 'submissions', adjustmentId);
    
    await setDoc(adjustmentDocRef, {
      teamId: teamId,
      totalScore: adjustment,
      isManualAdjustment: true,
      timestamp: new Date(),
      note: `Manual score adjustment of ${adjustment} points`
    });

    // Clear the input
    const team = teams.value.find(t => t.id === teamId);
    if (team) {
      team.manualAdjustment = '';
    }

    alert(`Score adjusted by ${adjustment} points`);
  } catch (error) {
    console.error('Error adjusting score:', error);
    alert('Error adjusting score. Please try again.');
  }
};

      onMounted(fetchTeams);
  
      return {
        teams,
        deleteTeam,
        toggleBonusPoints,
        onBonusChange,
        adjustScore,
      };
    },
  };
  </script>
  
  <style scoped>
  .gap-2 {
    gap: 0.5rem;
  }

  /* Add any other styles you need */
  </style>
